import LeaderboardApi from '@/api/leaderboard'
import { isFeatureEnabled, isPresentationCreatedAfterTurnOnFlag } from './featureToggles.module'
import { shouldShowLeaderboardVisibilityToggle } from '@/business-logic/leaderboard'

const _state = {
  isLeaderboardVisible: false,
  leaderboard: {},
  isShowingIndividualLeaderboardForTeamPlay: false,
  isAddingPoint: false,
}

const MUTATION_TYPES = {
  TOGGLE_LEADERBOARD_VISIBILITY: 'TOGGLE_LEADERBOARD_VISIBILITY',
  SET_LEADERBOARD: 'SET_LEADERBOARD',
  TOGGLE_INDIVIDUAL_LEADERBOARD_FOR_TEAMPLAY: 'TOGGLE_INDIVIDUAL_LEADERBOARD_FOR_TEAMPLAY',
  SET_IS_ADDING_POINT: 'SET_IS_ADDING_POINT',
}

export const UNLEASH_FLAG_TOGGLE_LEADERBOARD_ON_PRESENTING = 'toggle-leaderboard-on-presenting'
export const UNLEASH_FLAG_CAN_USE_LIVE_STATS_LEADERBOARD = 'livestats-leaderboard'

const _getters = {
  isTeamPlay(_, __, rootState) {
    return rootState.presentation?.teamPlay
  },
  isAddingPoint(state) {
    return state.isAddingPoint
  },
  isShowingTeamList(state, getters) {
    return getters.isTeamPlay && !state.isShowingIndividualLeaderboardForTeamPlay
  },
  isShowingIndividualLeaderboardForTeamPlay(state) {
    return state.isShowingIndividualLeaderboardForTeamPlay
  },
  canUseLeaderboardToggleOnPresenting(_, __, ___, rootGetters) {
    return isFeatureEnabled(rootGetters, UNLEASH_FLAG_TOGGLE_LEADERBOARD_ON_PRESENTING)
  },

  canUseLiveStatsLeaderboard(_, __, ___, rootGetters) {
    return isFeatureEnabled(rootGetters, UNLEASH_FLAG_CAN_USE_LIVE_STATS_LEADERBOARD) &&
        isPresentationCreatedAfterTurnOnFlag(rootGetters, UNLEASH_FLAG_CAN_USE_LIVE_STATS_LEADERBOARD)
  },

  isShowLeaderboardVisibilityToggle(state, getters, rootState, rootGetters) {
    if (!getters.canUseLeaderboardToggleOnPresenting) return false
    return shouldShowLeaderboardVisibilityToggle({
      slides: rootState.presentation.slides,
      isLive: rootGetters.isPresentingOrBackstage,
    })
  },

  isLeaderboardVisible(state, getters, _, rootGetters) {
    if (!getters.canUseLeaderboardToggleOnPresenting) return false
    if (!rootGetters.isPresentingOrBackstage) return false
    return state.isLeaderboardVisible
  },

  isLeaderboardEnabled() {
    return (slide) => {
      return slide.isLeaderboardEnabled
    }
  },
  leaderboardData(state) {
    return state.leaderboard
  },

  rankingListCount(_, getters) {
    return getters?.rankingList?.length
  },

  rankingList(state, getters) {
    if (getters.isShowingTeamList) {
      return state.leaderboard?.teams
    }
    return state.leaderboard?.audiences
  },

  rankingListKey(state, getters) {
    return (item) => {
      if (getters.isShowingTeamList) {
        return `team-${item.id}`
      }
      return `player-${item.audience}`
    }
  },

  topPoint(state, getters) {
    return getters.rankingList[0].totalPoint
  },

  getPercentage(state, getters) {
    return (item) => {
      return `${(item.totalPoint / getters.topPoint) * 100}%`
    }
  },

  winnerStr(state, getters) {
    if (getters.isShowingTeamList) {
      return state.leaderboard.topTeamStr
    }
    return state.leaderboard.topAudienceStr
  },

  winnerCount(state, getters) {
    if (getters.isShowingTeamList) {
      return state.leaderboard.countTeamWinner
    }
    return state.leaderboard.countWinner
  },

  getItemName(state, getters) {
    return (item) => {
      if (getters.isShowingTeamList) {
        return item.name
      }
      return item.audienceName
    }
  },

  getManualPointData(state, getters, rootState, rootGetters) {
    const isShownEmojiAvatar = rootGetters['presentationSetting/isShownEmojiAvatar']

    return (item) => {
      const data = {
        slideId: rootState.slideActive.id,
        presentationId: rootState.presentation.id,
        totalPoint: item.totalPoint,
      }
      if (getters.isShowingTeamList) {
        data.teamId = item.id
        data.name = item.name
      } else {
        data.audienceId = item.audience
        data.name = isShownEmojiAvatar ? `${item.audienceEmoji} ${item.audienceName}` : item.audienceName
      }
      return data
    }
  },
}

const actions = {
  // show/hide the leaderboard during the quiz
  toggleLeaderboardVisibilityAction: async ({ commit, getters }, { isVisible }) => {
    if (!getters.canUseLeaderboardToggleOnPresenting) return
    commit(MUTATION_TYPES.TOGGLE_LEADERBOARD_VISIBILITY, isVisible)
  },

  toggleLeaderboardOnParticipantScreenAction: async ({ dispatch }, { presentation, isVisible }) => {
    dispatch('kvSync/createKeyValueAction', {
      path: `/leaderboards/${presentation.id}`,
      end_part: '',
      type: 'string',
      value: JSON.stringify({ isVisible }),
    }, { root: true })
  },

  getLatestLeaderboardAction: async ({ commit }, payload) => {
    const res = await LeaderboardApi.getLatestLeaderboard(payload)
    commit(MUTATION_TYPES.SET_LEADERBOARD, res.body)
  },

  // turn on/off the leaderboard for quiz slide in Editor
  toggleLeaderboardEnabledAction: async ({ dispatch }, { slide }) => {
    dispatch('updateSlideActiveActionV2', {
      slide: {
        ...slide,
        isLeaderboardEnabled: !slide.isLeaderboardEnabled,
      },
      $optimistic: true,
    }, { root: true })
  },

  toggleIndividualLeaderboardForTeamplayAction: async ({ commit }) => {
    commit(MUTATION_TYPES.TOGGLE_INDIVIDUAL_LEADERBOARD_FOR_TEAMPLAY)
  },

  addPointAction: async ({ commit }, {
    point, audienceId, teamId, presentationId, slideId,
  }) => {
    commit(MUTATION_TYPES.SET_IS_ADDING_POINT, true)
    const res = await LeaderboardApi.addManualPoint({
      point,
      audienceId,
      teamId,
      presentationId,
      slideId,
    })
    commit(MUTATION_TYPES.SET_IS_ADDING_POINT, false)
    return res
  },

}

const mutations = {
  [MUTATION_TYPES.TOGGLE_LEADERBOARD_VISIBILITY](state, isVisible) {
    if (typeof isVisible !== 'boolean') {
      state.isLeaderboardVisible = !state.isLeaderboardVisible
      return
    }
    state.isLeaderboardVisible = isVisible
  },

  [MUTATION_TYPES.SET_LEADERBOARD](state, leaderboard) {
    state.leaderboard = leaderboard
  },

  [MUTATION_TYPES.TOGGLE_INDIVIDUAL_LEADERBOARD_FOR_TEAMPLAY](state) {
    state.isShowingIndividualLeaderboardForTeamPlay = !state.isShowingIndividualLeaderboardForTeamPlay
  },

  [MUTATION_TYPES.SET_IS_ADDING_POINT](state, isAddingPoint) {
    state.isAddingPoint = isAddingPoint
  },
}

const leaderboardModule = {
  namespaced: true,
  state: _state,
  getters: _getters,
  actions,
  mutations,
}

export default leaderboardModule
